import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/*
  Arrival and route-change scroll position.

  Two separate bugs lived here, and together they landed the visitor at the
  bottom of the page on any reload.

  1. history.scrollRestoration defaults to "auto", so the browser restores the
     previous scroll offset after the page loads. On a single-page app that
     restore happens once the hero media has loaded and the document reaches its
     old height, which is AFTER React has mounted.

  2. html { scroll-behavior: smooth } then turned our correction into a visible
     animated scroll from the bottom of the page up to the hero. On a phone, any
     touch during that animation cancels it, which strands the visitor wherever
     the animation had reached. That is the reported symptom: arriving at the
     bottom of the site.

  So: tell the browser not to restore, and make every programmatic jump instant.
  Smooth scrolling still applies to anything the user themselves triggers.
*/
export default function ScrollToTop() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    const jump = () => {
      if (hash) {
        document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start", behavior: "instant" });
        return;
      }
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    };

    jump();
    // The hero video and images resolve after the first paint and change the
    // document height. Chrome can still apply a restore in that window, so the
    // position is reasserted once layout has settled.
    const frame = window.requestAnimationFrame(jump);
    const settle = window.setTimeout(jump, 250);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settle);
    };
  }, [hash, pathname]);

  return null;
}
